import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import { CallPostCommand, EligibilitySummaryRequest, NotePostCommand, EscalatedNotePostCommand, EscalatedNoteResolutionCommand, VobRequestNotePostCommand, VobRequestNoteResolutionCommand, VerificationLookupRequest, VerificationSaveCommand, WorklistRequest,EligibilitySummaryResponse, ApprovalUpdateCommand, VerificationDocRemoveCommand, EligibilityDataRequest, CalculateReimbursementCommand, CalculateReimbursementResponse, PreQuoteCompleteCommand, PreQuoteCreateCommand, PreQuoteCreateResponse, PreQuoteUpdateCommand } from './commands';
import { SameDayScheduledView, Verification, Worklist } from './model';
import { CallLog } from './model/verification/CallLog';
import {getToken} from '../../utils/AdalConfig';
import {VerifyService} from '../../utils/ServiceLocator'
import { Approval } from './model/verification/Approval';
import { VerificationDocument } from './model/verification/VerificationDocument';
import { SameDayScheduledCommand } from './commands/SameDayScheduledCommand';
import { WorklistPayerGrouped } from './model/WorklistPayerGrouped';

export const verifyApi = createApi({
    reducerPath:'verifyApi',
    baseQuery: fetchBaseQuery({
        baseUrl: VerifyService,
        prepareHeaders:(headers,{getState}) => {
            const token = getToken();
            headers.set('Authorization', 'Bearer ' + token);
            headers.set('content-type', 'application/json')
            return headers;
        }
    }),
    tagTypes:['worklist','worklistPayerGrouped','verification','lookup','eligibilitySummary'],
    endpoints:(builder) => ({
        worklist:builder.query<Worklist[],WorklistRequest>({
            query:(request:WorklistRequest) => 'worklist?type=' + request.type + '&center=' + request.center + '&filter=' + request.filter,
            providesTags:['worklist']
        }),
        WorklistPayerGrouped:builder.query<WorklistPayerGrouped[],WorklistRequest>({
            query:(request:WorklistRequest) => 'worklistPayerGrouped?type=' + request.type + '&center=' + request.center + '&filter=' + request.filter,
            providesTags:['worklistPayerGrouped']
        }),
        verificationFind:builder.query<Verification,string>({
            query:(id) => 'verification?request=' + id,
            providesTags:['verification']
        }),
        verificationLookup:builder.query<Worklist[],VerificationLookupRequest>({
            query:(request:VerificationLookupRequest) => 'verificationLookup?centerId=' + request.centerId + '&searchCriteria='+request.searchCriteria,
            providesTags:['lookup']
        }),
        eligibilitySummary:builder.query<EligibilitySummaryResponse,EligibilitySummaryRequest>({
            query:(request) => 'eligibilitySummary?partitionKey=' + request.partitionKey + '&payerId=' + request.payerId,
        }),
        sameDayScheduledFind:builder.query<SameDayScheduledView[],SameDayScheduledCommand>({
            query:(command) => 'scheduledSameDay?centerId=' + command.centerId + '&serviceDate=' + command.serviceDate + '&physicianName=' + command.physicianName,
        }),
        eligibilityData:builder.mutation<string,EligibilityDataRequest>({
            query:(body) => 
                {return {url:'eligibilityData', method:'POST', body:body,}}
        }),
        saveVerification:builder.mutation<Verification,VerificationSaveCommand>({
            query:(body) =>{
                return {url:'Verification', method:'POST', body:body,}
            },
            invalidatesTags:['worklist','verification']
        }),
        saveNote:builder.mutation<void,NotePostCommand>({
          query:(body) => {
            return {url:'Note',method:'POST',body:body,}
          },        
        }),
        saveEscalatedNote:builder.mutation<void,EscalatedNotePostCommand>({
          query:(body) => {
            return {url:'EscalatedNote',method:'POST',body:body,}
          },        
        }),
        saveEscalatedNoteResolution:builder.mutation<void,EscalatedNoteResolutionCommand>({
          query:(body) => {
            return {url:'EscalatedNoteResolution',method:'POST',body:body,}
          },
          invalidatesTags:['worklist']
        }),
        saveVobRequestNote:builder.mutation<void,VobRequestNotePostCommand>({
          query:(body) => {
            return {url:'VobRequestNote',method:'POST',body:body,}
          },        
        }),
        resolveVobRequestNote:builder.mutation<void,VobRequestNoteResolutionCommand>({
          query:(body) => {
            return {url:'VobRequestNoteResolution',method:'POST',body:body,}
          },
          invalidatesTags:['worklist']
        }),
        saveCallLog:builder.mutation<CallLog, CallPostCommand>({
            query:(body) => {
                return {url:'CallAdd',method:'POST',body:body,}
            },
        }),
        saveApproval:builder.mutation<Approval,ApprovalUpdateCommand>({
            query:(body) =>{
                return {url:'Approval',method:'POST',body:body,}
            },
        }),
        verifyDocRemove:builder.mutation<VerificationDocument,VerificationDocRemoveCommand>({
            query:(body) =>{
                return {url:'documentRemove',method:'POST',body:body,}
            }
        }),
        calculateReimbursement:builder.mutation<CalculateReimbursementResponse,CalculateReimbursementCommand>({
            query:(body) =>{
                return {url:'CalculateReimbursement',method:'POST',body:body,}
            }
        }),
        savePreQuoteComplete:builder.mutation<void,PreQuoteCompleteCommand>({
            query:(body) => {
                return {url:'PreQuoteComplete',method:'POST',body:body,}
            },
            invalidatesTags:['worklist','verification']
        }),
        createPreQuote:builder.mutation<PreQuoteCreateResponse,PreQuoteCreateCommand>({
            query:(body) => {
                return {url:'PreQuoteCreate',method:'POST',body:body,}
            },
            invalidatesTags:['worklist']
        }),
        updatePreQuote:builder.mutation<Verification,PreQuoteUpdateCommand>({
            query:(body) => {
                return {url:'PreQuoteUpdate',method:'POST',body:body,}
            },
            invalidatesTags:['verification']
        }),
    })
})

export const {
    useWorklistQuery, 
    useWorklistPayerGroupedQuery, 
    useVerificationFindQuery, 
    useVerificationLookupQuery,
    useSaveVerificationMutation, 
    useSaveNoteMutation, 
    useSaveEscalatedNoteMutation, 
    useSaveEscalatedNoteResolutionMutation, 
    useSaveVobRequestNoteMutation, 
    useResolveVobRequestNoteMutation, 
    useSaveCallLogMutation,
    useEligibilitySummaryQuery,
    useSameDayScheduledFindQuery,
    useSaveApprovalMutation, 
    useVerifyDocRemoveMutation,
    useEligibilityDataMutation,
    useCalculateReimbursementMutation,
    useSavePreQuoteCompleteMutation,
    useCreatePreQuoteMutation,
    useUpdatePreQuoteMutation
} = verifyApi;